/**
 * Field popover HTML builder — /map field click popup.
 *
 * AK-295 (design review redesign, founder staging UAT 2026-06-27):
 *   - P0 contradiction fix: «Культура: X» + контр-бейдж «Пар» рядом ломал
 *     презентацию когда field_visual_attrs MV резолвит DISTINCT-ON sowing
 *     с phase=fallow поверх crop'а. Теперь crop+phase — ОДНА связная строка
 *     «Посев», без независимого бейджа, противоречащего culture-строке.
 *     Пар-поле читается как «Пар» (без фейковой культуры), засеянное —
 *     как «{культура} · {фаза}».
 *   - P2 эмодзи 📐/🌾 → inline SVG-иконки (lucide ruler / sprout),
 *     currentColor, как везде в DS. Scope строго этот компонент (AK-298
 *     закрывает эмодзи на detail-странице / ИИ-панели — не пересекаемся).
 *   - P2 «красная 1» поверх крестика — устранена: MapLibre default
 *     closeButton выключен в v2.tsx, рисуем свой DS-styled крестик в шапке.
 *   - P2 primary-кнопка «Открыть подробнее» — variant=default DS Button
 *     (solid --primary, без focus-ring-подобного контура).
 *   - DS pill/card-язык: --popover/--border/--radius-lg карточка, фаза-pill
 *     как scope-чипы (--secondary). Все цвета через hsl(var(--token)) →
 *     авто dark-mode. Hover/focus — в fieldPopover.css (HTML вне React-tree,
 *     тот же приём что chips.css).
 *
 * NDVI numerics (avg / CV) и cloudiness % НЕ ship'ятся в MVT properties —
 * backend отдаёт только hex-цвета (`avg_ndvi_color` / `cv_ndvi_color`).
 * Реальные числа — per-field detail fetch (Stage 4 follow-up).
 *
 * Pure function → unit-тестируется без MapLibre + jsdom canvas. Полный
 * click→popup флоу покрыт Playwright E2E (PR #69).
 *
 * XSS-safety: каждая строка из `fields_styled` MVT props (label_*,
 * current_phase, id) HTML-escape'ится — они из DB, но могут пройти через
 * user-edited имена. Defence-in-depth.
 */

import { formatCoords, navigatorProviders } from '@/lib/geo/navigateLinks';

export interface FieldPopoverProps {
  id?: string;
  label_number?: string;
  label_crop?: string;
  label_area?: string;
  current_phase?: string;
}

/** AK-372: точка маршрута (центроид/клик) для навигаторной строки попапа. */
export interface FieldPopoverCoords {
  lat: number;
  lng: number;
}

/**
 * Sprint 2026-20 Task 20 — NFR-8 (a11y); ported into Sprint 2026-21 (T12) on
 * top of the V2 single-store coverage shape (`coverage: Map<key,
 * CoverageData>`).
 *
 * Optional context passed by the caller (v2.tsx) to compose a screen-reader
 * ARIA label that combines field metadata with raster coverage status:
 *
 *   - rasterMode active + field covered → «… NDVI данные есть на YYYY-MM-DD»
 *   - rasterMode active + field NOT covered → «… нет данных за YYYY-MM-DD»
 *   - rasterMode off, seeded → «… {культура}, фаза {фаза}»
 *   - rasterMode off, fallow → «… Пар»
 *
 * Coverage is computed by the caller from the V2 store
 * (`state.coverage.get(key)?.coveredFieldIds.has(id)`) — store layer is not
 * imported here so this module stays a pure HTML builder, unit-testable
 * without zustand setup.
 */
export interface FieldPopoverAriaContext {
  /** 'none' or null = raster mode off. Anything else = active palette. */
  rasterMode: string | null;
  /** ISO YYYY-MM-DD; required to phrase the «нет данных за DATE» message. */
  selectedDate: string | null;
  /** True iff this field's id is in coverage[key].coveredFieldIds. */
  hasCoverage: boolean;
}

const PHASE_LABELS: Record<string, string> = {
  planned: 'Запланировано',
  growing: 'Растёт',
  harvested: 'Убрано',
  fallow: 'Пар',
};

/**
 * Phases that read coherently as a status pill next to a crop name.
 * `fallow` is intentionally excluded: a fallow field has no crop, so we never
 * pair «Пар» with a culture name (that was the P0 contradiction). When the
 * field is fallow we render «Пар» as the whole statement, no separate pill.
 */
const PHASE_PILL_TONE: Record<string, 'growing' | 'planned' | 'harvested'> = {
  growing: 'growing',
  planned: 'planned',
  harvested: 'harvested',
};

/** Neutral statement when there is no crop AND the phase is unrecognised. */
const NO_SOWING_LABEL = 'Нет данных о посеве';

/**
 * Resolve the single statement to show when a field has NO crop. The phase
 * alone becomes the whole assertion — never paired with a fabricated
 * «Культура: Не указана» line and a contradicting badge (the founder-found
 * P0: «Культура: Нет данных» + бейдж «Убрано» = «убрано — что?»).
 *
 *   - known phase (harvested/growing/planned/fallow) → its PHASE_LABELS value
 *     («Убрано» / «Растёт» / «Запланировано» / «Пар»)
 *   - missing phase (null/empty)                      → «Пар» (default fallow)
 *   - present but unrecognised phase                  → «Нет данных о посеве»
 *
 * Returns the label string plus a `tone` for pill styling. `harvested` /
 * `growing` / `planned` keep their semantic tone; everything else reads as a
 * neutral / fallow pill.
 */
function noCropStatement(phase: string | undefined): {
  label: string;
  tone: 'growing' | 'planned' | 'harvested' | 'fallow';
} {
  if (!phase) {
    // Missing phase ⇒ treat as fallow (matches «нет посева» default).
    return { label: PHASE_LABELS.fallow, tone: 'fallow' };
  }
  const known = PHASE_LABELS[phase];
  if (known) {
    const tone = PHASE_PILL_TONE[phase] ?? 'fallow';
    return { label: known, tone };
  }
  // Phase value present but not a recognised enum — neutral, never invent crop.
  return { label: NO_SOWING_LABEL, tone: 'fallow' };
}

/**
 * Compose the spoken «Посев» fragment for the ARIA label — the screen-reader
 * mirror of `sowingRowHtml`. Same branches, so audio and visual never
 * contradict (AK-295 follow-up, founder unlocked NFR-8 2026-06-27; extended
 * 2026-06-28 for the no-crop × real-phase case the founder caught):
 *
 *   - NO crop (any phase) →  the phase is the whole statement:
 *       «Убрано» / «Растёт» / «Запланировано» / «Пар» / «Нет данных о посеве»
 *     never «культура не указана, …» + a contradicting status.
 *   - crop + fallow phase →  «Пар» (no fabricated culture name)
 *   - crop + known phase  →  «{культура}, фаза {фаза}»  (natural speech)
 *   - crop, unknown ph.   →  «{культура}»  (nothing to assert about the phase)
 *
 * Phase wording reuses PHASE_LABELS (the visible pill labels: Растёт /
 * Запланировано / Убрано / Пар) so the spoken phase matches the pill the
 * sighted user sees, not the older «культура …, статус: …» form.
 */
function composeSowingAria(props: FieldPopoverProps): string {
  const phase = props.current_phase;
  const rawCrop = props.label_crop;

  // No crop — the phase alone is the statement (founder-found contradiction).
  if (!rawCrop) {
    return noCropStatement(phase).label;
  }

  // Crop present, fallow phase — read «Пар», never «культура …, Пар».
  if (phase === 'fallow') {
    return PHASE_LABELS.fallow;
  }

  const tone = PHASE_PILL_TONE[phase ?? ''];
  if (tone) {
    return `${rawCrop}, фаза ${PHASE_LABELS[phase as string]}`;
  }
  // Crop with an unknown phase — name the crop, assert nothing about the phase.
  return rawCrop;
}

/**
 * Compose ARIA label per NFR-8. Pure function so it can be unit-tested without
 * DOM. Caller threads in coverage state from the V2 store.
 *
 * Output forms (founder unlocked + harmonised with the visible popup 2026-06-27):
 *   1. raster off, seeded:  «Поле {name}, площадь {area}, {культура}, фаза {фаза}»
 *   1b. raster off, fallow: «Поле {name}, площадь {area}, Пар»
 *   2. raster + cov:        «Поле {name}, площадь {area}, NDVI данные есть на {date}»
 *   3. raster + none:       «Поле {name}, площадь {area}, нет данных за {date}»
 *
 * The non-raster sowing fragment is produced by `composeSowingAria`, the
 * spoken twin of `sowingRowHtml`, so the screen reader never announces the
 * «культура X, статус: Пар» contradiction the visible redesign removed.
 */
export function composeFieldPopoverAriaLabel(
  props: FieldPopoverProps,
  ctx: FieldPopoverAriaContext,
): string {
  const name = props.label_number ?? '—';
  const area = props.label_area ?? '—';
  const base = `Поле ${name}, площадь ${area}`;

  const rasterModeActive = ctx.rasterMode !== null && ctx.rasterMode !== 'none';
  if (rasterModeActive) {
    const datePhrase = ctx.selectedDate ?? 'выбранную дату';
    if (ctx.hasCoverage) {
      return `${base}, NDVI данные есть на ${datePhrase}`;
    }
    return `${base}, нет данных за ${datePhrase}`;
  }

  return `${base}, ${composeSowingAria(props)}`;
}

/** Escape HTML-meaningful chars to neutralise injection from MVT props. */
export function escapeHtml(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c),
  );
}

/** Map raw phase enum value to Russian label, falling back to raw. */
export function phaseLabel(phase: string | undefined): string {
  if (!phase) return PHASE_LABELS.fallow;
  return PHASE_LABELS[phase] ?? phase;
}

/**
 * Inline 14px lucide icons (currentColor) — заменяют эмодзи 📐/🌾 (AK-295 P2).
 * Inline SVG, т.к. popup рисуется как HTML-строка вне React-tree → lucide-react
 * компоненты недоступны. Пути из lucide-static (ruler / sprout).
 */
const ICON_RULER =
  '<svg class="fp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z"/><path d="m14.5 12.5 2-2"/><path d="m11.5 9.5 2-2"/><path d="m8.5 6.5 2-2"/><path d="m17.5 15.5 2-2"/></svg>';

const ICON_SPROUT =
  '<svg class="fp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 20h10"/><path d="M10 20c5.5-2.5.8-6.4 3-10"/><path d="M9.5 9.4c1.1.8 1.8 2.2 2.3 3.7-2 .4-3.5.4-4.8-.3-1.2-.6-2.3-1.9-3-4.2 2.8-.5 4.4 0 5.5.8z"/><path d="M14.1 6a7 7 0 0 0-1.1 4c1.9-.1 3.3-.6 4.3-1.4 1-1 1.6-2.3 1.7-4.6-2.7.1-4 1-4.9 2z"/></svg>';

const ICON_X =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';

const ICON_EXTERNAL =
  '<svg class="fp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>';

// AK-372: pin-иконка для координатной строки (lucide map-pin).
const ICON_PIN =
  '<svg class="fp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 10c0 4.4-8 12-8 12s-8-7.6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>';

// AK-373: navigation-иконка для primary «Маршрут» (lucide navigation).
const ICON_NAV =
  '<svg class="fp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>';

/**
 * AK-372 / AK-380 — компактная строка «координаты + маршрут в навигатор».
 *
 * Точка = центроид поля (или клик по полигону, если центроид недоступен).
 *
 * AK-373 п.7: primary «Маршрут» отдаёт выбор навигатора ОС.
 *   - Android → geo:-chooser; десктоп → web (openRoute).
 *   - iOS (AK-380) → БОЛЬШЕ НЕ share-sheet. openRoute на iOS открывает
 *     Яндекс Карты дефолтом, а всегда-видимый ряд «Открыть в:» ниже — это и есть
 *     in-app меню провайдеров (Яндекс Карты / 2ГИС / Google / Apple).
 *
 * Кнопки-провайдера несут app + web deep-link'и в data-атрибутах (v2.tsx вешает
 * click → openNavigator: мобайл открывает приложение, десктоп — web-версию).
 * Набор и порядок провайдеров — из navigatorProviders (единый источник; Apple
 * добавлен, Яндекс = «Яндекс Карты»). `&` в URL экранируется как `&amp;`.
 */
function coordsRowHtml(coords: FieldPopoverCoords): string {
  const label = formatCoords(coords.lat, coords.lng);
  const providers = navigatorProviders(coords.lat, coords.lng);
  const btn = ({ key, label: text, link }: (typeof providers)[number]) =>
    `<button type="button" class="fp-nav-btn" data-nav="${key}" data-app="${escapeHtml(
      link.app,
    )}" data-web="${escapeHtml(link.web)}">${escapeHtml(text)}</button>`;
  return `
    <div class="fp-row" data-testid="field-popover-coords">
      ${ICON_PIN}
      <span class="fp-coords">${escapeHtml(label)}</span>
    </div>
    <button type="button" class="fp-route-btn" data-testid="field-popover-route" data-lat="${coords.lat}" data-lng="${coords.lng}">
      ${ICON_NAV}Маршрут
    </button>
    <div class="fp-nav-label">Открыть в:</div>
    <div class="fp-nav-row">
      ${providers.map(btn).join('\n      ')}
    </div>`;
}

/**
 * Build the «Посев» statement — P0 fix. Returns crop + phase as ONE coherent
 * line, never a crop name with a contradicting independent badge.
 *
 *   - NO crop (any phase) →  the phase is the WHOLE statement, a single pill
 *       «Убрано»/«Растёт»/«Запланировано»/«Пар»/«Нет данных о посеве» — no
 *       «Культура: Не указана» line, no contradicting second badge. (Founder
 *       caught «Культура: Нет данных» + бейдж «Убрано» = «убрано — что?».)
 *   - crop + fallow phase →  «Пар» (нейтральный pill, без culture-имени)
 *   - crop + growable      →  «{культура}» + согласованный фаза-pill
 *   - crop, unknown ph.    →  «{культура}» (без pill — нечего утверждать)
 */
function sowingRowHtml(props: FieldPopoverProps): string {
  const rawCrop = props.label_crop;
  const phase = props.current_phase;

  // No crop — render only the phase as a single statement pill. Никакой
  // строки «Культура: …» и никакого контр-бейджа рядом.
  if (!rawCrop) {
    const { label, tone } = noCropStatement(phase);
    return `
      <div class="fp-row">
        ${ICON_SPROUT}
        <span class="fp-row-label">Посев:</span>
        <span class="fp-pill fp-pill-${tone}" data-testid="field-popover-status">${escapeHtml(
          label,
        )}</span>
      </div>`;
  }

  // Crop present + fallow — «Пар» as the whole statement, no culture name.
  if (phase === 'fallow') {
    return `
      <div class="fp-row">
        ${ICON_SPROUT}
        <span class="fp-row-label">Посев:</span>
        <span class="fp-pill fp-pill-fallow" data-testid="field-popover-status">Пар</span>
      </div>`;
  }

  const crop = escapeHtml(rawCrop);
  const tone = PHASE_PILL_TONE[phase ?? ''];
  const pill = tone
    ? `<span class="fp-pill fp-pill-${tone}" data-testid="field-popover-status">${escapeHtml(
        phaseLabel(phase),
      )}</span>`
    : '';

  return `
    <div class="fp-row">
      ${ICON_SPROUT}
      <span class="fp-row-label">Посев:</span>
      <span class="fp-crop">${crop}</span>
      ${pill}
    </div>`;
}

/**
 * Опции сборки попапа.
 *
 * `sowSlot` (запрос founder 2026-07-27 «нет кнопки посеять с карты»): вместо
 * СТАТИЧНОЙ строки «Посев» из MVT-свойств отрисовать пустой контейнер-слот,
 * в который `v2.tsx` порталом монтирует живой React-блок `FieldSowBlock`
 * (посев по сезону/зонам + кнопка «Отметить: посеяно»). Утверждение о посеве
 * на экране остаётся ОДНО — просто оно становится актуальным и кликабельным.
 *
 * Без опции (значение по умолчанию) поведение прежнее — чистый HTML-попап без
 * зависимостей от React: так его дёргают юнит-тесты и любой не-React вызов.
 */
export interface FieldPopoverOptions {
  sowSlot?: boolean;
  /**
   * AK-772 (решение founder 28.08, «выверка полей с карты», этап 1): слот под
   * живой React-блок действий над полем — «Переименовать», «Архивировать»,
   * «Геометрия». Механика та же, что у `sowSlot`: попап отдаёт пустой
   * контейнер, `v2.tsx` монтирует в него порталом `FieldActionsBlock`, чтобы
   * действия могли пользоваться правами, диалогами DS и react-query, а карта
   * / тайлы / фильтры остались нетронутыми.
   *
   * Полный редактор контура НА САМОЙ карте — этап 2; «Геометрия» пока ведёт в
   * редактор контура карточки поля (`/fields/{id}?tab=geometry`).
   */
  actionsSlot?: boolean;
}

/**
 * Render the popover HTML. Returns a self-contained string ready for
 * `maplibregl.Popup.setHTML()`. Styling lives in `fieldPopover.css`
 * (imported globally), same pattern as `chips.css` — HTML mounts outside the
 * React tree but still under `:root`, so `hsl(var(--token))` resolves and
 * dark mode tracks automatically.
 *
 * Close: own DS-styled crest button (`data-testid="field-popover-close"`);
 * MapLibre's default `closeButton` is disabled in v2.tsx to avoid the stray
 * artifact over the crest. v2.tsx wires the click to `popup.remove()`.
 */
export function buildFieldPopoverHtml(
  props: FieldPopoverProps,
  ariaContext?: FieldPopoverAriaContext,
  coords?: FieldPopoverCoords,
  options?: FieldPopoverOptions,
): string {
  const fieldId = escapeHtml(props.id ?? '');
  const number = escapeHtml(props.label_number ?? '—');
  const area = escapeHtml(props.label_area ?? '—');

  const link = fieldId
    ? `<a href="/fields/${fieldId}" target="_blank" rel="noopener" data-testid="field-popover-link" class="fp-cta">${ICON_EXTERNAL}Открыть подробнее</a>`
    : '';

  // Sprint 2026-20 Task 20 / NFR-8 — ARIA label for screen readers. Computed
  // by composeFieldPopoverAriaLabel from MVT props + V2-store-derived context.
  // If context is omitted (legacy callers, pure-HTML unit tests) — wrapper
  // renders without role/aria-label (graceful degradation).
  const ariaAttrs = ariaContext
    ? ` role="dialog" aria-label="${escapeHtml(
        composeFieldPopoverAriaLabel(props, ariaContext),
      )}"`
    : '';

  return `
    <div class="fp-root" data-testid="field-popover"${ariaAttrs}>
      <div class="fp-head">
        <div class="fp-title">${number}</div>
        <button type="button" class="fp-close" data-testid="field-popover-close" aria-label="Закрыть">${ICON_X}</button>
      </div>
      <div class="fp-row">
        ${ICON_RULER}
        <span class="fp-row-label">Площадь:</span>
        <span class="fp-area">${area}</span>
      </div>
      ${
        options?.sowSlot
          ? '<div class="fp-sow-slot" data-testid="field-popover-sow-slot"></div>'
          : sowingRowHtml(props)
      }
      ${coords ? coordsRowHtml(coords) : ''}
      ${link}
      ${
        options?.actionsSlot
          ? '<div class="fp-actions-slot" data-testid="field-popover-actions-slot"></div>'
          : ''
      }
    </div>
  `.trim();
}
